/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Text,
  TextAlignment,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import React, {useEffect} from 'react';
import styled from 'styled-components';
import 'swiper/css/effect-fade';
import {SwiperSlide} from 'swiper/react';
import SpaceLike from '../../components/Space';
import useMediaQuery from '../../hooks/useMediaQuery';
import {Variants, motion, useAnimation} from 'framer-motion';
import {headingVariants, subheadingVariants} from '../animations';

const StyledText = styled(Text)`
  color: white;
`;

const stepVariants: Variants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      delay: 0.5,
      duration: 0.33,
      ease: 'easeInOut',
    },
  },
};

type StepProps = {
  index?: number;
  title: string;
  description: string;
  children: React.ReactNode;
  isActive: boolean;
};

const Step: React.FC<StepProps> = ({
  index,
  isActive,
  title,
  description,
  children,
}) => {
  const step = useAnimation();
  const heading = useAnimation();
  const subheading = useAnimation();

  const {isMobile} = useMediaQuery();

  useEffect(() => {
    if (isActive) {
      const sequence = () => {
        step.start('animate');
        heading.start('animate');
        subheading.start('animate');
      };
      sequence();
    }
  }, [heading, isActive, step, subheading]);

  return (
    <SwiperSlide>
      <SpaceLike
        direction="vertical"
        customStyles={{
          alignItems: 'center',
          justifyContent: 'center',
          overflowY: 'scroll',
          paddingTop: '24px',
          paddingBottom: 'calc(50px + env(safe-area-inset-bottom))',
        }}
        width={'100%'}
        size={isMobile ? 24 : 48}
      >
        <div
          style={{
            position: 'relative',
            height: '286px',
            pointerEvents: 'none',
          }}
        >
          {children}
        </div>
        <SpaceLike size={8} direction="vertical">
          <SpaceLike size={4} direction="vertical">
            <motion.div
              key="step"
              variants={stepVariants}
              initial="initial"
              animate={step}
            >
              {index != null && (
                <StyledText
                  appearance={TokenTextAppearance.label200}
                  textAlign={TextAlignment.center}
                >
                  Step {index + 1}
                </StyledText>
              )}
            </motion.div>
            <motion.div
              key="heading"
              variants={headingVariants}
              initial="initial"
              animate={heading}
            >
              <StyledText
                appearance={TokenTextAppearance.headline300}
                textAlign={TextAlignment.center}
                bold
              >
                {title}
              </StyledText>
            </motion.div>
          </SpaceLike>

          <motion.div
            key="subheading"
            variants={subheadingVariants}
            initial="initial"
            animate={subheading}
          >
            <StyledText
              appearance={TokenTextAppearance.copy200}
              textAlign={TextAlignment.center}
            >
              {description}
            </StyledText>
          </motion.div>
        </SpaceLike>
      </SpaceLike>
    </SwiperSlide>
  );
};

Step.displayName = 'SwiperSlide';

export default Step;
